import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, ShieldAlert } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';

/**
 * Prompts an already-authenticated patient to re-enter their password to
 * re-derive their encryption key in memory.
 *
 * The private key is a non-extractable CryptoKey persisted only in IndexedDB.
 * When IndexedDB is unavailable (Chrome/Safari on iOS in Private Browsing or
 * Lockdown Mode) or the entry was evicted, the key can't survive a refresh and
 * can't be recovered from storage — but a fresh derivation from the password
 * always works. This dialog performs that re-derivation without forcing a full
 * re-login. See ABOBO-PORTAL-62.
 */
function UnlockKeysDialog() {
  const { t } = useTranslation();
  const { needsUnlock, unlockKeys, logout } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  const storageUnavailable = typeof indexedDB === 'undefined';

  const form = useForm({ defaultValues: { password: '' } });

  // keysLoading briefly flips needsUnlock false mid-derive; keep it open
  // through the attempt so it closes only once the key is in memory.
  const open = needsUnlock || form.formState.isSubmitting;

  const onSubmit = async ({ password }: { password: string }) => {
    try {
      await unlockKeys(password);
      form.reset();
    } catch {
      form.setError('password', {
        message: t('secure.unlock.errors.incorrect'),
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={() => {}}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('secure.unlock.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {storageUnavailable
              ? t('secure.unlock.privateModeMessage')
              : t('secure.unlock.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {storageUnavailable && (
          <div className="flex items-start gap-2 rounded-sm bg-amber-50 p-3 text-sm text-amber-900">
            <ShieldAlert className="mt-0.5 size-4 shrink-0" />
            <span>{t('secure.unlock.privateModeHint')}</span>
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="password"
              rules={{ required: t('secure.unlock.errors.passwordRequired') }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('secure.unlock.fieldNames.password')}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        className="pr-12"
                        autoFocus
                        {...field}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => logout()}
                disabled={form.formState.isSubmitting}
              >
                {t('secure.unlock.buttons.logout')}
              </Button>
              <Button
                type="submit"
                loading={form.formState.isSubmitting}
                loadingText={t('secure.unlock.buttons.unlocking')}
              >
                {t('secure.unlock.buttons.unlock')}
              </Button>
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export { UnlockKeysDialog };
